/**
 * Device detection utilities
 */

/**
 * Detects if the current device is a mobile device based on User Agent and screen characteristics.
 */
export function isMobileDevice(): boolean {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return false;
    }

    // Check User Agent for mobile devices
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || '';
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;
    const isMobileUA = mobileRegex.test(userAgent);

    // Check for touch capability as a secondary indicator
    const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Check screen size (typical mobile width threshold)
    const isSmallScreen = window.innerWidth <= 1024;

    // Consider it mobile if UA matches OR (has touch AND small screen)
    return isMobileUA || (hasTouchScreen && isSmallScreen);
}

/**
 * Returns the current scale factor needed to fit the game into the viewport.
 * The game is designed for a base resolution of 1920x1080.
 */
export function getMobileScaleFactor(baseWidth = 1920, baseHeight = 1080): number {
    if (typeof window === 'undefined') return 1;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate scale to fit width and height
    const scaleX = viewportWidth / baseWidth;
    const scaleY = viewportHeight / baseHeight;

    // Use the smaller scale to ensure the game fits entirely
    return Math.min(scaleX, scaleY);
}

/**
 * Custom hook-like function to get responsive scaling info.
 * Call this in a useEffect or useState initializer.
 */
export function getDeviceInfo() {
    return {
        isMobile: isMobileDevice(),
        isTouch: isTouchDevice(),
        scaleFactor: getMobileScaleFactor(),
        viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1920,
        viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 1080,
    };
}

/**
 * Detects if the device has touch capability (regardless of screen size).
 * Use this to enable touch-specific UI/gestures.
 */
export function isTouchDevice(): boolean {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return false;
    }
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}
